import { useCallback } from "react";
import { addHours, format, parse } from "date-fns";
import { uk } from "date-fns/locale/uk";
import { useSearchParams } from "react-router-dom";
import { Divider, Typography } from "theme";
import { CrossSmall } from "theme/icons";

import { Box, CircularProgress, Grid2 } from "@mui/material";
import { styled } from "@mui/material/styles";

import useSelectedEquipment from "../../utils/hooks/useSelectedEquipment.ts";

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "88px",
    width: "88px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

const StyledButton = styled("button")(({ theme }) => {
  return {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(0.5),
  };
});

const SelectedEquipment = () => {
  const [searchParams] = useSearchParams();
  const { selectedEquipment, isFetching, onEquipmentChange } = useSelectedEquipment();

  const getSelectedTime = useCallback(
    (range: number) => {
      const selectedDate = searchParams.get("d");
      const selectedTime = searchParams.get("t");

      const startDate = parse(`${selectedDate} ${selectedTime}`, "yyyy-MM-dd HH:mm", new Date());
      const endDate = addHours(startDate, range);

      return `${format(startDate, "HH:mm", {
        locale: uk,
      })} - ${format(endDate, "HH:mm")}`;
    },
    [searchParams],
  );

  if (isFetching) {
    return (
      <Grid2 container justifyContent="center">
        <CircularProgress color="secondary" />
      </Grid2>
    );
  }

  if (selectedEquipment.length === 0) return null;

  return (
    <>
      <Box>
        {selectedEquipment.map((eq) => {
          return (
            <Grid2
              key={eq.id}
              columnGap={1.5}
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Grid2 my={1.25} container columnGap={1.5} flexWrap="nowrap">
                  <Box>
                    <StyledImage
                      src={eq.imageUrl}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = "/camera-image.jpg";
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body1" weight="regular" textTransform="uppercase">
                      {eq.title}
                    </Typography>

                    <Typography my={0.5} variant="body2" weight="regular">
                      Кількість: {eq.count}
                    </Typography>

                    <Typography my={0.5} variant="body2" weight="regular">
                      Час оренди: {getSelectedTime(eq.range)}
                    </Typography>

                    <Typography variant="body2" weight="semiBold" textTransform="uppercase">
                      {eq.pricePerUnit} {eq.isFixedPrice ? "грн" : "грн/год"}
                    </Typography>
                  </Box>
                </Grid2>
              </Box>
              <StyledButton onClick={() => onEquipmentChange(eq.id)}>
                <CrossSmall />
              </StyledButton>
            </Grid2>
          );
        })}
      </Box>
      <Box my={2.5}>
        <Divider />
      </Box>
    </>
  );
};

export default SelectedEquipment;
